import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { moderationApi } from "@bw/shared/api";
import type {
  ReportCreate,
  ReportSubmissionResponse,
} from "@bw/shared/types/generated";
import { useRef, useState, type FormEvent } from "react";

interface ReportDialogProps {
  open: boolean;
  subjectLabel: string;
  targetId: string;
  targetType: ReportCreate["target_type"];
  onClose(): void;
}

/** Context-bound report form. Opaque target IDs remain transport-only. */
export function ReportDialog({
  open,
  subjectLabel,
  targetId,
  targetType,
  onClose,
}: ReportDialogProps) {
  const [reason, setReason] = useState("");
  const inFlight = useRef(false);
  const idempotencyKey = useRef(globalThis.crypto.randomUUID());
  const commandRef = useRef<ReportCreate | null>(null);
  const report = useMutation<ReportSubmissionResponse, Error, ReportCreate>({
    mutationFn: (command) => moderationApi.submitReport(command),
  });

  const resetAndClose = () => {
    if (report.isPending) return;
    setReason("");
    idempotencyKey.current = globalThis.crypto.randomUUID();
    commandRef.current = null;
    report.reset();
    onClose();
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = reason.trim();
    const command =
      commandRef.current ??
      (trimmed && trimmed.length <= 2000
        ? {
            idempotency_key: idempotencyKey.current,
            reason: trimmed,
            target_id: targetId,
            target_type: targetType,
          }
        : null);
    if (!command || inFlight.current) return;
    commandRef.current = command;
    inFlight.current = true;
    report.mutate(command, { onSettled: () => (inFlight.current = false) });
  };

  return (
    <Dialog
      open={open}
      onClose={resetAndClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="report-dialog-title"
    >
      <DialogTitle id="report-dialog-title">Report {subjectLabel}</DialogTitle>
      {report.isSuccess ? (
        <>
          <DialogContent>
            <Alert severity="success">Report submitted.</Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={resetAndClose}>Close</Button>
          </DialogActions>
        </>
      ) : (
        <Stack component="form" onSubmit={submit}>
          <DialogContent>
            <Stack spacing={2}>
              <Typography color="text.secondary">
                Tell the safety team what happened. Do not include passwords,
                payment details, or unrelated private information.
              </Typography>
              <TextField
                autoFocus
                label="Reason"
                multiline
                minRows={4}
                fullWidth
                slotProps={{ htmlInput: { maxLength: 2000 } }}
                value={reason}
                disabled={report.isError && commandRef.current !== null}
                onChange={(event) => setReason(event.target.value)}
              />
              {report.isError ? (
                <Alert severity="error" role="alert">
                  We couldn&apos;t submit your report. Please try again.
                </Alert>
              ) : null}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              type="button"
              color="inherit"
              disabled={report.isPending}
              onClick={resetAndClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={
                (!reason.trim() && commandRef.current === null) ||
                report.isPending
              }
            >
              {report.isPending
                ? "Submitting…"
                : report.isError
                  ? "Retry report"
                  : "Submit report"}
            </Button>
          </DialogActions>
        </Stack>
      )}
    </Dialog>
  );
}
